import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { LOGO_URL } from "@/lib/phoneModels";
import { SOCIAL } from "@/lib/siteAssets";

export default function Footer() {
    return (
        <footer className="bg-neutral-950 text-neutral-300 mt-16" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-8">
                    <div className="col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={LOGO_URL}
                                alt="Vendagain"
                                className="h-10 w-10 rounded-md bg-white p-1 object-contain"
                            />
                            <div>
                                <div className="text-white font-semibold text-lg tracking-tight">
                                    Vendagain
                                </div>
                                <div className="text-[10px] text-green-400 font-medium uppercase tracking-[0.14em]">
                                    Perth Vending Solutions
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-neutral-400 leading-relaxed max-w-xs">
                            Free snack & drink vending machines for Perth
                            workplaces — and custom phone case printing machines
                            for premium venues. Weekly restocks, real product
                            monitoring, ice-cold drinks in summer.
                        </p>
                    </div>

                    <div>
                        <h4 className="text-white text-sm font-semibold mb-3">
                            Explore
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/food-drink-vending" className="hover:text-white transition-colors">
                                    Food & Drink Vending
                                </Link>
                            </li>
                            <li>
                                <Link to="/phone-case-vending" className="hover:text-white transition-colors">
                                    Phone Case Machines
                                </Link>
                            </li>
                            <li>
                                <Link to="/designer" className="hover:text-white transition-colors">
                                    Case Designer
                                </Link>
                            </li>
                            <li>
                                <Link to="/host-a-machine" className="hover:text-white transition-colors">
                                    Host a Machine
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white text-sm font-semibold mb-3">
                            For Businesses
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/host-a-machine#benefits" className="hover:text-white transition-colors">
                                    Why host a machine
                                </Link>
                            </li>
                            <li>
                                <Link to="/host-a-machine#form" className="hover:text-white transition-colors">
                                    Get a free machine
                                </Link>
                            </li>
                            <li>
                                <Link to="/host-a-machine#faq" className="hover:text-white transition-colors">
                                    FAQ
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white text-sm font-semibold mb-3">
                            Get in touch
                        </h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2">
                                <MapPin size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <span>Perth, Western Australia</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Mail size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <div className="space-y-1.5">
                                    <div>
                                        <a href="mailto:info@vendagain.com.au" className="hover:text-white transition-colors block">
                                            info@vendagain.com.au
                                        </a>
                                        <div className="text-[11px] text-neutral-500">Enquiries & new locations</div>
                                    </div>
                                    <div>
                                        <a href="mailto:support@vendagain.com.au" className="hover:text-white transition-colors block">
                                            support@vendagain.com.au
                                        </a>
                                        <div className="text-[11px] text-neutral-500">Existing customer support</div>
                                    </div>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <Phone size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <a href="tel:+61410784187" className="hover:text-white transition-colors">
                                    0410 784 187
                                </a>
                            </li>
                            <li className="pt-2">
                                <a
                                    href={SOCIAL.tiktok}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors group"
                                    data-testid="footer-tiktok"
                                >
                                    <span className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-green-500 group-hover:border-green-500 transition-colors">
                                        <TikTokIcon />
                                    </span>
                                    <span>
                                        <span className="block font-medium">Follow on TikTok</span>
                                        <span className="block text-[11px] text-neutral-500">@vendagain</span>
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-10 pt-6 border-t border-neutral-800 text-xs text-neutral-500 flex flex-col sm:flex-row justify-between gap-3">
                    <span>© {new Date().getFullYear()} Vendagain. All rights reserved.</span>
                    <span>Servicing Perth & Greater Western Australia</span>
                </div>
            </div>
        </footer>
    );
}

/* Simple inline TikTok icon (SVG) to avoid pulling in an extra icon lib. */
function TikTokIcon() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M19.6 6.32a5.9 5.9 0 0 1-3.5-1.15A5.9 5.9 0 0 1 14.3 2h-3.4v13.36a2.7 2.7 0 1 1-2.7-2.7c.28 0 .56.05.82.13V9.36a6.1 6.1 0 0 0-.82-.06 6.09 6.09 0 1 0 6.1 6.09V9.06a9.36 9.36 0 0 0 5.3 1.65V7.28c-.01 0-.01-.05 0-.96Z" />
        </svg>
    );
}
